import {type MouseEvent, useEffect, useRef, useState} from 'react';
import {useAtom, useSetAtom} from 'jotai';
import {X, Trash2, AlertCircle} from 'lucide-react';
import {
  selectedTaskIdAtom,
  taskDetailOpenAtom,
  dashCallIdAtom,
  dashCallOpenAtom,
  dashCallContextAtom,
  processedRecordsAtom,
  resolvedQueueIdsAtom,
  type ProcessedRecord,
} from '@lib/atoms';
import {
  useTask,
  useUpdateTask,
  useDeleteTask,
  useTaskCreationContext,
  useTaskActorSid,
} from '@api/tasks/hooks';
import {IS_MOCK} from '@api/mock-mode';
import {
  type TaskPriority,
  type TaskStatus,
  TASK_PRIORITY_LABELS,
  TASK_STATUS_LABELS,
  TASK_STATUS_ORDER,
} from '@entities/task/model/types';
import {
  TaskStatusBadge,
  TaskPriorityBadge,
  TaskObserverBadge,
} from '@entities/task';
import {MOCK_TODAY, cn, formatCallNumber, formatDateTime} from '@lib/utils';
import {toast} from 'sonner';
import {Button} from '@ui/shadcn/button';
import {TaskCallField} from './TaskCallField';
import {DateTimeField} from '@ui/local/DateTimeField';
import {Textarea} from '@ui/local/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/shadcn/select';
import {parseISO, format, isValid} from 'date-fns';
import {
  useTaskPermissions,
  TASK_DELETE_DENIED,
  TASK_FINALIZE_DENIED,
} from '../model/useTaskPermissions';

import {useTaskDraft} from '../model/useTaskDraft';
import {useTaskComments} from '../model/useTaskComments';
import {TaskCommentItem} from './TaskCommentItem';
import {TaskCommentEditor} from './TaskCommentEditor';
import {TaskTitleField} from './TaskTitleField';
import {ModalPresence} from '@ui/local/ModalPresence';
import {useBodyScrollLock} from '@hooks/useBodyScrollLock';

const PRIORITY_OPTIONS: TaskPriority[] = ['high', 'medium', 'low'];

const ASSIGNEE_NONE = '__none__';

/**
 * Расширенная модалка задачи (раунд 4 §4): селекты статуса/приоритета/менеджера,
 * чип связанного звонка, секция комментариев с @mention и Ctrl+Enter,
 * dirty-aware футер с переключением «Закрыть задачу» ⇄ «Сохранить изменения».
 */
export const TaskDetailModal = () => {
  const actorSid = useTaskActorSid();
  const [open, setOpen] = useAtom(taskDetailOpenAtom);
  const [selectedId, setSelectedId] = useAtom(selectedTaskIdAtom);
  const setDashCallId = useSetAtom(dashCallIdAtom);
  const [callOpen, setDashCallOpen] = useAtom(dashCallOpenAtom);
  const setCallContext = useSetAtom(dashCallContextAtom);
  const callOpenerRef = useRef<HTMLElement | null>(null);
  const setProcessed = useSetAtom(processedRecordsAtom);
  const setResolved = useSetAtom(resolvedQueueIdsAtom);

  const permissions = useTaskPermissions();
  const taskQuery = useTask(selectedId, open);
  const creationContext = useTaskCreationContext(open);
  const assignees = creationContext.data?.assignees ?? [];
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  const task = taskQuery.isError ? undefined : taskQuery.data;
  const draft = useTaskDraft(task, open);
  const {title, text, managerId, priority, status, dueDate, callId} =
    draft.fields;
  const canComment = Boolean(task && permissions.canComment(task));
  const commentForm = useTaskComments(task, open, canComment);
  const comments = task?.comments ?? [];

  // Возвращаем фокус в задачу после просмотра звонка, не сбрасывая черновик.
  useEffect(() => {
    if (open && !callOpen && callOpenerRef.current) {
      callOpenerRef.current.focus({preventScroll: true});
      callOpenerRef.current = null;
    }
  }, [open, callOpen]);

  useEffect(() => {
    if (open && taskQuery.isError) {
      toast.error('Задача недоступна или не удалось проверить доступ');
      setOpen(false);
    }
  }, [open, taskQuery.isError, setOpen]);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const commentsBoxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setErrors({});
    setConfirmDelete(false);
    if (!open) setSelectedId(null);
  }, [open, task?.id, setSelectedId]);

  // Auto-scroll comments to bottom when count changes
  useEffect(() => {
    if (commentsBoxRef.current) {
      commentsBoxRef.current.scrollTop = commentsBoxRef.current.scrollHeight;
    }
  }, [comments.length]);

  useBodyScrollLock(open);

  const renderModal = () => {
    if (!open || !task) return null;

    const cannotClose = !permissions.canFinalize(task);
    const canDelete = permissions.canDelete(task);
    const canEdit = permissions.canEdit(task);
    const canChangeStatus = TASK_STATUS_ORDER.some((value) =>
      permissions.canSetStatus(task, value),
    );
    const isOverdue =
      dueDate &&
      dueDate < MOCK_TODAY &&
      status !== 'done' &&
      status !== 'partial';

    const isDirty = draft.isDirty;

    // Дедлайн приходит строкой: date-only, naive-datetime или ISO с таймзоной.
    // parseISO даёт корректный момент; дату и время показываем в локали
    // пользователя. Прежний split по 'T' игнорировал offset — из-за него дата
    // «скакала» в соседний месяц у tz-aware значений.
    const dueInstant = dueDate ? parseISO(dueDate) : null;
    const dueDatePart =
      dueInstant && isValid(dueInstant) ? format(dueInstant, 'yyyy-MM-dd') : '';
    const dueTimePart =
      dueInstant && isValid(dueInstant) && dueDate.includes('T')
        ? format(dueInstant, 'HH:mm')
        : '';

    const handleDueDateChange = (newDatePart: string) => {
      draft.setField(
        'dueDate',
        dueTimePart ? `${newDatePart}T${dueTimePart}:00` : newDatePart,
      );
    };

    const handleDueTimeChange = (time: string) => {
      const datePart = dueDatePart || MOCK_TODAY;
      draft.setField('dueDate', time ? `${datePart}T${time}:00` : datePart);
    };

    const validate = (): boolean => {
      const e: Record<string, string> = {};
      if (title.trim().length < 3) e.title = 'Минимум 3 символа';
      if (!dueDate) e.dueDate = 'Укажите срок';
      setErrors(e);
      return Object.keys(e).length === 0;
    };

    const persistAll = async (overrideStatus?: TaskStatus) => {
      if (canEdit && !validate()) return false;
      const nextStatus = overrideStatus ?? status;
      if (
        nextStatus !== task.status &&
        !permissions.canSetStatus(task, nextStatus)
      ) {
        toast.error(TASK_FINALIZE_DENIED);
        return false;
      }
      const input = draft.getUpdate(canEdit, overrideStatus);
      if (!Object.keys(input).length) return false;
      try {
        await updateTask.mutateAsync({id: task.id, input});
      } catch {
        toast.error(
          'Не удалось сохранить изменения. Обновите задачу и проверьте доступ.',
        );
        await taskQuery.refetch();
        return false;
      }
      return true;
    };

    const handleSave = async () => {
      const ok = await persistAll();
      if (!ok) return;
      toast.success('Задача обновлена');
      setOpen(false);
    };

    const handleCloseTask = async () => {
      const ok = await persistAll('done');
      if (!ok) return;
      toast.success(`Задача «${title.trim()}» закрыта`);

      // Mock-режим: если задача привязана к звонку — переносим запись очереди в
      // «Обработанные» локально. В реальном режиме это делает бэк
      // (source=task-closed), а хук обновления инвалидирует очереди/архив.
      if (
        IS_MOCK &&
        callId &&
        task.creator === 'rop' &&
        permissions.canEdit(task)
      ) {
        const {DASH_QUEUE_URGENT, DASH_QUEUE_MANAGEMENT, DASH_QUEUE_PRACTICES} =
          await import('@pages/dashboard-page/model/mock');
        const all = [
          ...DASH_QUEUE_URGENT,
          ...DASH_QUEUE_MANAGEMENT,
          ...DASH_QUEUE_PRACTICES,
        ];
        const queueItem = all.find((q) => q.callId === callId);
        if (queueItem) {
          const lastComment =
            comments.length > 0 ? comments[comments.length - 1].text : null;
          const record: ProcessedRecord = {
            id: `pr-${Date.now()}`,
            at: new Date().toISOString(),
            problemTitle: queueItem.problemTitle,
            callId: queueItem.callId,
            managerShort: queueItem.managerShort,
            action: lastComment
              ? `Решено (задача закрыта). ${lastComment}`
              : 'Решено (задача закрыта)',
            source: 'task_closed',
          };
          setProcessed((prev) => [record, ...prev]);
          setResolved((s) => {
            const next = new Set(s);
            next.add(queueItem.id);
            return next;
          });
        }
      }

      setOpen(false);
    };

    const handleDelete = async () => {
      if (!permissions.canDelete(task)) {
        toast.error(TASK_DELETE_DENIED);
        return;
      }
      try {
        await deleteTask.mutateAsync(task.id);
      } catch {
        toast.error('Не удалось удалить задачу');
        return;
      }
      toast.success('Задача удалена');
      setOpen(false);
    };

    const handleOpenCall = (event: MouseEvent<HTMLButtonElement>) => {
      if (!callId) return;
      callOpenerRef.current = event.currentTarget;
      setCallContext(null);
      setDashCallId(callId);
      setDashCallOpen(true);
    };

    return (
      <div
        className='modal-overlay fixed inset-0 bg-black/45 backdrop-blur-[2px] flex items-center justify-center px-4 py-6'
        style={{zIndex: 'var(--z-modal-task)'}}
        inert={callOpen}
        aria-hidden={callOpen || undefined}
        onClick={(e) => {
          if (e.target === e.currentTarget) setOpen(false);
        }}
      >
        <div className='modal-panel bg-[var(--color-surface)] rounded-xl shadow-2xl w-full max-w-[760px] max-h-[calc(100vh-3rem)] flex flex-col overflow-hidden'>
          {/* Статус и приоритет — вместе в шапке, без повторной плашки снизу. */}
          <div className='flex items-start justify-between gap-3 px-5 py-3 border-b border-[var(--color-border)] bg-[var(--color-surface)]'>
            <div className='flex flex-wrap items-center gap-2 min-w-0'>
              <TaskStatusBadge status={status} />
              <TaskPriorityBadge priority={priority} />
              <TaskObserverBadge
                actorSid={actorSid}
                observerUserSid={task.observerUserSid}
              />
              <span
                className='min-w-0 truncate text-xs text-[var(--color-muted-foreground)]'
                title={task.id}
              >
                #{formatCallNumber(task.id)}
              </span>
            </div>
            <Button
              variant='ghost'
              size='icon'
              onClick={() => setOpen(false)}
              className='size-7 shrink-0 text-[var(--color-secondary)]'
            >
              <X size={16} />
            </Button>
          </div>

          {/* Body */}
          <div className='flex-1 overflow-y-auto px-5 py-3 space-y-3 min-h-0'>
            {/* Title */}
            <div>
              <TaskTitleField
                key={task.id}
                canEdit={canEdit}
                value={title}
                onChange={(value) => draft.setField('title', value)}
                invalid={!!errors.title}
              />
              {errors.title && (
                <p className='text-xs text-[var(--color-danger)] mt-1 flex items-center gap-1'>
                  <AlertCircle size={11} /> {errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <div className='text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)] mb-1.5'>
                Описание
              </div>
              <Textarea
                readOnly={!canEdit}
                value={text}
                onChange={(e) => draft.setField('text', e.target.value)}
                placeholder='Что нужно сделать, зачем, какой ожидается результат...'
                rows={2}
                className='w-full min-h-16 max-h-40 resize-y leading-relaxed'
              />
            </div>

            {/* Status + Priority */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <div>
                <div className='text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)] mb-1.5'>
                  Статус
                </div>
                <Select
                  disabled={!canChangeStatus}
                  value={status}
                  onValueChange={(v) =>
                    draft.setField('status', v as TaskStatus)
                  }
                >
                  <SelectTrigger className='w-full'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASK_STATUS_ORDER.map((s) => (
                      <SelectItem
                        key={s}
                        value={s}
                        disabled={!permissions.canSetStatus(task, s)}
                      >
                        {TASK_STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {cannotClose && (
                  <p className='mt-1.5 text-xs text-[var(--color-muted-foreground)]'>
                    {TASK_FINALIZE_DENIED}
                  </p>
                )}
              </div>
              <div>
                <div className='text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)] mb-1.5'>
                  Приоритет
                </div>
                <Select
                  disabled={!canEdit}
                  value={priority}
                  onValueChange={(v) =>
                    draft.setField('priority', v as TaskPriority)
                  }
                >
                  <SelectTrigger className='w-full'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((p) => (
                      <SelectItem key={p} value={p}>
                        {TASK_PRIORITY_LABELS[p]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Assignee + Due */}
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              <div>
                <div className='text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)] mb-1.5'>
                  Ответственный
                </div>
                <Select
                  disabled={!canEdit || assignees.length < 2}
                  value={managerId || ASSIGNEE_NONE}
                  onValueChange={(value) => draft.setField('managerId', value)}
                >
                  <SelectTrigger className='w-full'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {!managerId && (
                      <SelectItem value={ASSIGNEE_NONE}>Не назначен</SelectItem>
                    )}
                    {managerId &&
                      !assignees.some(
                        (person) => person.userSid === managerId,
                      ) && (
                        <SelectItem value={managerId}>
                          {task.manager || managerId}
                        </SelectItem>
                      )}
                    {assignees.map((emp) => (
                      <SelectItem key={emp.userSid} value={emp.userSid}>
                        {emp.displayName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className='text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)] mb-1.5'>
                  Срок
                </div>
                <DateTimeField
                  disabled={!canEdit}
                  date={dueDatePart}
                  time={dueTimePart}
                  onDateChange={handleDueDateChange}
                  onTimeChange={handleDueTimeChange}
                  minDate={MOCK_TODAY}
                  invalid={!!errors.dueDate}
                  triggerClassName={
                    isOverdue
                      ? 'text-[var(--score-bad)] font-medium'
                      : undefined
                  }
                  contentZIndex='calc(var(--z-modal-task) + 1)'
                />
                {isOverdue && (
                  <p className='text-[11px] text-[var(--score-bad)] mt-1 font-medium'>
                    ⚠ Просрочена
                  </p>
                )}
                {errors.dueDate && (
                  <p className='text-xs text-[var(--color-danger)] mt-1 flex items-center gap-1'>
                    <AlertCircle size={11} /> {errors.dueDate}
                  </p>
                )}
              </div>
            </div>

            {/* Call link */}
            <div>
              <div className='text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)] mb-1.5'>
                Звонок
              </div>
              <TaskCallField
                key={task.id}
                value={callId}
                canEdit={canEdit}
                onChange={(value) => draft.setField('callId', value)}
                onOpen={handleOpenCall}
              />
            </div>

            {/* Comments */}
            <div>
              <div className='text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)] mb-1.5'>
                Комментарии{comments.length > 0 && ` · ${comments.length}`}
              </div>

              <div
                ref={commentsBoxRef}
                className={cn(
                  'flex flex-col gap-2 mb-2 max-h-[240px] overflow-y-auto',
                  comments.length > 0 &&
                    'p-2 bg-[#FAFAFA] border border-[var(--color-border)] rounded-lg',
                )}
              >
                {comments.length === 0 && (
                  <p className='text-xs italic text-[var(--color-muted-foreground)]'>
                    Пока нет комментариев
                  </p>
                )}
                {comments.map((comment) => (
                  <TaskCommentItem
                    key={comment.id}
                    task={task}
                    comment={comment}
                  />
                ))}
              </div>
              <TaskCommentEditor form={commentForm} />
            </div>
          </div>

          {/* Footer */}
          <div className='flex items-center gap-2 px-5 py-3 border-t border-[var(--color-border)] bg-[#FAFAFA]'>
            {canDelete &&
              (confirmDelete ? (
                <div className='flex items-center gap-1.5'>
                  <span className='text-[11px] text-[var(--color-danger)]'>
                    Удалить?
                  </span>
                  <Button
                    variant='destructive'
                    size='xs'
                    onClick={handleDelete}
                    disabled={deleteTask.isPending}
                    className='bg-[var(--color-danger)]'
                  >
                    Да
                  </Button>
                  <Button
                    variant='outline'
                    size='xs'
                    onClick={() => setConfirmDelete(false)}
                    className='text-[var(--color-secondary)]'
                  >
                    Нет
                  </Button>
                </div>
              ) : (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setConfirmDelete(true)}
                  className='gap-1 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10 hover:text-[var(--color-danger)]'
                >
                  <Trash2 size={12} /> Удалить
                </Button>
              ))}

            <span className='text-[11px] whitespace-nowrap text-[var(--color-muted-foreground)]'>
              Создана:{' '}
              <strong className='text-[var(--color-foreground)] font-semibold'>
                {formatDateTime(task.createdAt)}
              </strong>
            </span>

            <div className='flex-1' />

            <Button
              variant='outline'
              size='sm'
              onClick={() => setOpen(false)}
              className='text-[var(--color-secondary)]'
            >
              Отмена
            </Button>
            <Button
              size='sm'
              onClick={isDirty ? handleSave : handleCloseTask}
              disabled={
                !title.trim() ||
                updateTask.isPending ||
                (cannotClose && !isDirty)
              }
              title={
                cannotClose && !isDirty
                  ? TASK_FINALIZE_DENIED
                  : isDirty
                    ? 'Сохранить изменения'
                    : 'Перевести задачу в статус «Выполнено»'
              }
            >
              {updateTask.isPending
                ? 'Сохранение...'
                : isDirty
                  ? 'Сохранить изменения'
                  : '✓ Закрыть задачу'}
            </Button>
          </div>
        </div>
      </div>
    );
  };
  return <ModalPresence>{renderModal()}</ModalPresence>;
};
